import { useState, useEffect, useCallback, useRef } from "react";
import { useLoginToastNotifications } from "@/hooks/useLoginToastNotifications";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getRetentionConfig, SubscriptionTier } from "@/lib/retention";
import { Sparkles, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MobileHeader } from "@/components/mobile/MobileHeader";
import { MobileMenu } from "@/components/mobile/MobileMenu";
import { ImageUploader } from "@/components/mobile/ImageUploader";
import { CategoryCarousel } from "@/components/mobile/CategoryCarousel";
import { StyleGrid } from "@/components/mobile/StyleGrid";
import { MobileScrollSelect } from "@/components/mobile/MobileScrollSelect";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";


import { DESIGN_CATEGORIES } from "@/data/designStyles";
import { useDesigner } from "@/contexts/DesignerContext";
import { uploadImageToStorage } from "@/lib/storage";
import { calculateRemainingCredits } from "@/lib/tier-limits";
import { readCachedCredits, writeProfileSnapshot } from "@/lib/profile-snapshot";
import { useLockedStylesFlag } from "@/hooks/useFeatureFlag";
import { useGenerationSurveyGate } from "@/hooks/useGenerationSurveyGate";
import { GenerationSurveyDialog } from "@/components/GenerationSurveyDialog";
import { UpgradeProDialog } from "@/components/UpgradeProDialog";
import { useRoomRefreshFlag } from "@/hooks/useRoomRefreshFlag";
import { BottomTabBar } from "@/components/mobile/BottomTabBar";


const ROOM_TYPES = ["Floor Plan / 3D Render", "Living Room", "Bedroom", "Kids Room / Nursery", "Kitchen", "Dining Room", "Study Room", "Bathroom", "Office", "Home Theater / Media Room", "Gaming Room", "Walk-in Closet", "Laundry / Utility Room", "Garage", "Gym", "Balcony", "Patio / Terrace / Rooftop", "Garden / Backyard", "Foyer / Entryway", "Hallway", "Reception / Waiting Area", "Cafe / Restaurant", "Retail Store", "Co-working Space"];

const VARIATION_OPTIONS = [
  { value: "1", label: "1 Variation", credits: 1 },
  { value: "2", label: "2 Variations", credits: 2 },
  { value: "3", label: "3 Variations", credits: 3 },
];

// Downscale image utility
const downscaleImage = (file: File, maxDimension: number = 1024): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height / width) * maxDimension;
          width = maxDimension;
        } else {
          width = (width / height) * maxDimension;
          height = maxDimension;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const MobileDesigner = () => {
  const navigate = useNavigate();
  const { state, setState, resetState } = useDesigner();

  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: flagLoading, enforced: lockEnforced, isAdmin, isStyleLocked } = useLockedStylesFlag();
  const { loading: roomRefreshFlagLoading, visible: roomRefreshVisible } = useRoomRefreshFlag();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [lockedStyleName, setLockedStyleName] = useState<string | undefined>(undefined);
  const [upgradeContext, setUpgradeContext] = useState<"style" | "variation">("style");
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [pendingGeneration, setPendingGeneration] = useState(false);
  const pendingGenerationRef = useRef(false);

  // User & Credits
  const [user, setUser] = useState<any>(null);
  useLoginToastNotifications(user?.id || null);
  const [cachedProfile] = useState(readCachedCredits);
  const [credits, setCredits] = useState(cachedProfile?.credits ?? 0);
  const [designsThisMonth, setDesignsThisMonth] = useState(0);
  const [totalGenerations, setTotalGenerations] = useState(0);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>(
    (cachedProfile?.tier as SubscriptionTier) ?? "free"
  );
  const [loadingUser, setLoadingUser] = useState(cachedProfile === null);

  const [unreadCount, setUnreadCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const { loading: surveyLoading, needsSurvey, markCompleted } = useGenerationSurveyGate(
    user?.id,
    totalGenerations
  );

  // Menu
  const [menuOpen, setMenuOpen] = useState(false);

  // Image
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Design Options
  const [roomType, setRoomType] = useState("");
  const [variations, setVariations] = useState("1");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [customIdeas, setCustomIdeas] = useState("");

  // Clamp variations to 1 for free users; paid users keep choice.
  useEffect(() => {
    if (subscriptionTier === 'free' && !isAdmin && variations !== "1") {
      setVariations("1");
    }
  }, [subscriptionTier, isAdmin, variations]);

  // Generation
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Room auto-detection
  const [detectingRoom, setDetectingRoom] = useState(false);

  const detectRoomType = useCallback(
    async (imageUrl: string) => {
      if (roomType) return; // don't overwrite manual / loaded selection
      setDetectingRoom(true);
      try {
        const { data, error } = await supabase.functions.invoke("detect-room", {
          body: { imageUrl },
        });
        if (!error && data?.detectedRoomType && ROOM_TYPES.includes(data.detectedRoomType)) {
          setRoomType(data.detectedRoomType);
          toast.success(`Room detected: ${data.detectedRoomType}`);
        }
      } catch (e) {
        console.error("Room detection failed", e);
      } finally {
        setDetectingRoom(false);
      }
    },
    [roomType],
  );

  // Loaded project tracking
  const [loadedProjectId, setLoadedProjectId] = useState<string | null>(null);
  const [loadedVariations, setLoadedVariations] = useState<string[] | null>(null);
  const [loadedIsPublic, setLoadedIsPublic] = useState<boolean>(false);

  // Track online/offline status
  const hasRefreshedOnReconnect = useRef(false);

  useEffect(() => {
    const goOnline = () => {
      setIsOffline(false);
      // Refresh profile data once on reconnect
      if (!hasRefreshedOnReconnect.current && user) {
        hasRefreshedOnReconnect.current = true;
        loadProfile(user.id);
      }
    };
    const goOffline = () => {
      setIsOffline(true);
      hasRefreshedOnReconnect.current = false;
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Refresh profile on focus so totals/credits stay fresh after returning
    // from a generation session.
    const refreshOnFocus = () => {
      if (document.visibilityState === "visible" && navigator.onLine && user) {
        loadProfile(user.id);
      }
    };
    document.addEventListener("visibilitychange", refreshOnFocus);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [user]);

  // Get current category styles
  const currentCategory = DESIGN_CATEGORIES.find((cat) => cat.id === selectedCategory);

  // Warm browser cache for the selected category + the next one,
  // so switching categories shows preview images instantly.
  useEffect(() => {
    if (!selectedCategory) return;
    const idx = DESIGN_CATEGORIES.findIndex((c) => c.id === selectedCategory);
    if (idx === -1) return;

    const toPreload = [
      DESIGN_CATEGORIES[idx],
      DESIGN_CATEGORIES[(idx + 1) % DESIGN_CATEGORIES.length],
    ];

    const schedule = (cb: () => void) => {
      const ric = (window as any).requestIdleCallback as
        | ((cb: () => void, opts?: { timeout: number }) => number)
        | undefined;
      if (typeof ric === "function") {
        ric(cb, { timeout: 1500 });
      } else {
        setTimeout(cb, 600);
      }
    };

    schedule(() => {
      for (const category of toPreload) {
        for (const style of category.styles) {
          if (!style.image) continue;
          const img = new Image();
          img.decoding = "async";
          img.src = style.image;
        }
      }
    });
  }, [selectedCategory]);

  // Listen for auth state changes (handles sign-out, token expiry)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || (!session && navigator.onLine)) {
        navigate("/auth");
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Load profile totals/credits (used on mount and when regaining focus).
  const loadProfile = useCallback(async (userId: string) => {
    if (!navigator.onLine) return;
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("designs_this_month, total_generations, bonus_generations, subscription_tier")
        .eq("id", userId)
        .single();

      if (profile) {
        setSubscriptionTier(profile.subscription_tier as SubscriptionTier);
        setDesignsThisMonth(profile.designs_this_month ?? 0);
        setTotalGenerations(profile.total_generations ?? 0);
        setCredits(calculateRemainingCredits(profile.subscription_tier, profile.designs_this_month, profile.bonus_generations));
        writeProfileSnapshot(userId, {
          subscription_tier: profile.subscription_tier,
          bonus_generations: profile.bonus_generations ?? 0,
          designs_this_month: profile.designs_this_month ?? 0,
          total_generations: profile.total_generations ?? 0,
        });
      }

    } catch (error) {
      console.error("Error loading profile:", error);
    }
  }, []);

  // Load user and credits
  useEffect(() => {
    const loadUser = async () => {
      try {
        // Use getSession() instead of getUser() — works offline from cache
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          // Only redirect if online; if offline, stay on page
          if (navigator.onLine) {
            navigate("/auth");
          }
          return;
        }

        const currentUser = session.user;
        setUser(currentUser);

        // Only fetch profile/notifications if online
        if (navigator.onLine) {
          await loadProfile(currentUser.id);

          // Fetch unread notification count
          const { count } = await supabase
            .from("user_notifications")
            .select("*", { count: "exact", head: true })
            .eq("user_id", currentUser.id)
            .eq("is_read", false);

          setUnreadCount(count || 0);
        }
      } catch (error) {
        console.error("Error loading user:", error);
        // Don't show error toast if offline — it's expected
        if (navigator.onLine) {
          toast.error("Failed to load user data");
        }
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, [navigate, loadProfile]);

  // Load project from URL parameter
  const loadProject = useCallback(
    async (projectId: string) => {
      try {
        const { data, error } = await supabase.from("saved_designs").select("*").eq("id", projectId).maybeSingle();

        if (error) throw error;
        if (!data) {
          toast.error("Project not found");
          return;
        }

        // Restore state from saved project
        setUploadedImage(data.original_image_url);
        setState({ uploadedImage: data.original_image_url });
        setRoomType(data.room_type);
        setCustomIdeas(data.custom_prompt || "");

        // Find and select the correct category for the style.
        // Guard: a previously-free style may now be Pro — clear it & prompt.
        const style = data.design_style;
        if (style && isStyleLocked(style)) {
          console.warn("[locked-styles] Cleared locked style from loaded project (mobile)", {
            style,
            projectId,
            source: "load_project",
          });
          setSelectedStyle(null);
          setState({ selectedStyle: null });
          setLockedStyleName(style);
          setUpgradeContext("style");
          setUpgradeOpen(true);
          toast.info(
            `"${style}" is now a Pro style. Pick another style to continue.`,
            { duration: 6000 }
          );
        } else {
          for (const category of DESIGN_CATEGORIES) {
            const foundStyle = category.styles.find((s) => s.name === style);
            if (foundStyle) {
              setSelectedCategory(category.id);
              setSelectedStyle(style);
              setState({ selectedStyle: style });
              break;
            }
          }
        }

        // Store loaded variations if they exist
        const variations = data.all_variations as string[] | null;
        if (variations && variations.length > 0) {
          setLoadedVariations(variations);
        } else {
          setLoadedVariations(null);
        }

        setLoadedIsPublic(!!data.is_public);
        setLoadedProjectId(projectId);
        toast.success("Project loaded successfully!");
      } catch (error) {
        console.error("Error loading project:", error);
        toast.error("Failed to load project");
      }
    },
    [setState],
  );

  // Check for project load parameter, or prefill from Explore "Try This Style"
  const prefilledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user) return;
    const loadParam = searchParams.get("load");
    if (loadParam) {
      if (loadParam !== loadedProjectId) loadProject(loadParam);
      return;
    }

    const styleParam = searchParams.get("style");
    const roomParam = searchParams.get("room");
    const promptParam = searchParams.get("prompt");
    if (!styleParam && !roomParam && !promptParam) return;

    // Wait for the locked-styles flag to evaluate before deciding whether to
    // accept a style coming in via URL (e.g. from Explore "Try This Style").
    if (styleParam && flagLoading) return;

    const key = `${styleParam || ""}|${roomParam || ""}|${promptParam || ""}`;
    if (prefilledKeyRef.current === key) return;
    prefilledKeyRef.current = key;

    if (roomParam) setRoomType(roomParam);
    if (promptParam) setCustomIdeas(promptParam);
    if (styleParam) {
      if (isStyleLocked(styleParam)) {
        console.warn("[locked-styles] Blocked style from URL param (mobile)", {
          style: styleParam,
          source: "url_param",
          timestamp: new Date().toISOString(),
        });
        setLockedStyleName(styleParam);
        setUpgradeContext("style");
        setUpgradeOpen(true);
        toast.info(
          `"${styleParam}" is a Pro style. Pick a free style below or upgrade to unlock it.`,
          { duration: 6000 }
        );
        // Strip ?style= so a refresh doesn't reintroduce the locked selection.
        const next = new URLSearchParams(searchParams);
        next.delete("style");
        setSearchParams(next, { replace: true });
      } else {
        for (const category of DESIGN_CATEGORIES) {
          const foundStyle = category.styles.find((s) => s.name === styleParam);
          if (foundStyle) {
            setSelectedCategory(category.id);
            setSelectedStyle(styleParam);
            setState({ selectedStyle: styleParam });
            break;
          }
        }
      }
    }
  }, [searchParams, user, loadProject, loadedProjectId, setState, flagLoading, isStyleLocked, setSearchParams]);


  // Auto-save function
  const handleAutoSave = async (images: string[], userId: string) => {
    try {
      const now = new Date();
      const retention = getRetentionConfig(subscriptionTier, true);
      const autoTitle = `Auto-saved Design - ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

      const { error } = await supabase.from("saved_designs").insert({
        user_id: userId,
        title: autoTitle,
        original_image_url: uploadedImage,
        transformed_image_url: images[0] || null,
        all_variations: images,
        room_type: roomType,
        design_style: selectedStyle,
        custom_prompt: customIdeas || null,
        is_auto_saved: true,
        saved_permanently: retention.savedPermanently,
        expires_at: retention.expiresAt,
        retention_policy: retention.retentionPolicy,
      });

      if (error) throw error;
      toast.success(`✅ Design ${retention.displayText.toLowerCase()}`);
    } catch (error) {
      console.error("Auto-save error:", error);
      // Silent fail - don't block user flow
    }
  };

  // Handle image upload
  const handleImageUpload = useCallback(
    async (file: File) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image must be less than 10MB");
        return;
      }

      setUploadingImage(true);
      try {
        const dataUrl = await downscaleImage(file);
        setUploadedImage(dataUrl);
        setState({ uploadedImage: dataUrl });
        toast.success("Image uploaded successfully");

        // Auto-detect room type in parallel
        detectRoomType(dataUrl);

        // Upload to storage in background and update URL silently (like web)
        uploadImageToStorage(dataUrl, "original").then((storageUrl) => {
          if (storageUrl) {
            setUploadedImage(storageUrl);
            setState({ uploadedImage: storageUrl });
          }
        });
      } catch (error) {
        console.error("Error processing image:", error);
        toast.error("Failed to process image");
      } finally {
        setUploadingImage(false);
      }
    },
    [setState],
  );

  // Handle direct data URL from camera (native)
  const handleImageDataUrl = useCallback(
    async (dataUrl: string, source?: 'camera' | 'gallery') => {
      setUploadingImage(true);
      try {
        setUploadedImage(dataUrl);
        setState({ uploadedImage: dataUrl });
        toast.success(source === 'gallery' ? "Photo selected from gallery" : "Photo captured successfully");

        // Auto-detect room type in parallel
        detectRoomType(dataUrl);

        // Upload to storage in background
        uploadImageToStorage(dataUrl, "original").then((storageUrl) => {
          if (storageUrl) {
            setUploadedImage(storageUrl);
            setState({ uploadedImage: storageUrl });
          }
        });
      } catch (error) {
        console.error("Error processing image:", error);
        toast.error("Failed to process image");
      } finally {
        setUploadingImage(false);
      }
    },
    [setState],
  );

  // Handle image remove
  const handleRemoveImage = useCallback(() => {
    setUploadedImage(null);
    setState({ uploadedImage: null });
  }, [setState]);

  // Handle category select
  const handleCategorySelect = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedStyle(null); // Reset style when category changes
  }, []);

  // Handle style select
  const handleStyleSelect = useCallback(
    (styleName: string) => {
      setSelectedStyle(styleName);
      setState({ selectedStyle: styleName });
    },
    [setState],
  );

  // Check if can generate
  const canGenerate =
    uploadedImage && roomType && selectedStyle && selectedCategory && credits >= parseInt(variations) && !generating;

  // Reset designer to a fresh state (does not delete saved designs)
  const handleStartOver = useCallback(() => {
    setUploadedImage(null);
    setRoomType("");
    setSelectedCategory(null);
    setSelectedStyle(null);
    setCustomIdeas("");
    setVariations("1");
    setLoadedProjectId(null);
    setLoadedVariations(null);
    setLoadedIsPublic(false);
    resetState();
    prefilledKeyRef.current = null;
    navigate("/designer", { replace: true });
    toast.success("Started a new project");
  }, [navigate, resetState]);

  // Handle generate - Navigate to Results page immediately for progressive display
  const handleGenerate = async () => {
    if (generating) return;

    if (!user) {
      navigate("/auth");
      return;
    }

    if (!uploadedImage) {
      toast.error("Upload a photo of your room to get started");
      return;
    }
    if (!roomType) {
      toast.error("Select your room type");
      return;
    }
    if (!selectedCategory) {
      toast.error("Choose a design category first");
      return;
    }
    if (!selectedStyle) {
      toast.error("Pick a style from the selected category");
      return;
    }

    // Survey gate: once per lifetime for eligible free users before 2nd+ generation.
    if (needsSurvey && !pendingGenerationRef.current) {
      setSurveyOpen(true);
      return;
    }
    pendingGenerationRef.current = false;
    setPendingGeneration(false);


    // Defence in depth: re-check the lock right before we navigate to results.
    if (isStyleLocked(selectedStyle)) {
      console.warn("[locked-styles] Generate blocked client-side (mobile)", {
        style: selectedStyle,
        roomType,
        userId: user?.id,
        source: "generate_handler",
        timestamp: new Date().toISOString(),
      });
      setLockedStyleName(selectedStyle);
      setUpgradeContext("style");
      setUpgradeOpen(true);
      toast.error(
        "This design style is part of Pro. Pick a free style below or upgrade to unlock it.",
        { duration: 6000 }
      );
      return;
    }
    if (credits < parseInt(variations)) {
      toast.error(`You need ${parseInt(variations)} credit(s) but only have ${credits}. Upgrade for more!`);
      return;
    }

    const numVariations = parseInt(variations);

    // Navigate immediately to Results with generation params
    setLoadedVariations(null);
    setLoadedProjectId(null);

    navigate("/results", {
      state: {
        originalImage: uploadedImage,
        transformedImages: [], // Empty - will be generated on Results page
        roomType,
        designStyle: selectedStyle,
        // Generation params for progressive loading
        isGenerating: true,
        customIdeas,
        totalVariations: numVariations,
        userId: user.id,
        subscriptionTier,
      },
    });
  };

  // Handle sign out
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <div className={`min-h-screen bg-[#FFF7F1] ${roomRefreshVisible ? "pb-48" : "pb-28"}`}>
      {/* Fixed Header */}
      <MobileHeader
        credits={credits}
        loading={loadingUser}
        onMenuClick={() => setMenuOpen(true)}
        onNotificationClick={() => navigate("/notifications")}
        onExploreClick={() => navigate("/explore")}
        onBlogClick={() => navigate("/blog")}
        unreadCount={unreadCount}
      />

      {/* Mobile Menu */}
      <MobileMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        subscriptionTier={subscriptionTier}
        onSignOut={handleSignOut}
      />

      {/* Scrollable Content */}
      <div className="pt-safe-header space-y-6">
        {/* Upload Section */}
        <section className="px-4 mt-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-foreground">Your Room Photo <span className="text-destructive">*</span></h2>
            {(uploadedImage || roomType || selectedStyle || customIdeas || loadedProjectId) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Start Over
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Start a new project?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This clears your current photo, room type, style, and notes so you can begin fresh.
                      Your previous design stays auto-saved in <span className="font-medium">Projects</span>.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleStartOver}>Start Over</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <ImageUploader
            image={uploadedImage}
            loading={uploadingImage}
            onUpload={handleImageUpload}
            onImageDataUrl={handleImageDataUrl}
            onRemove={handleRemoveImage}
          />
          {uploadedImage && !roomType && (
            <p className="text-xs text-muted-foreground mt-2 animate-pulse">↓ Now complete the required fields below</p>
          )}
        </section>

        {/* View Results - shown when loaded project has variations */}
        {loadedVariations && loadedVariations.length > 0 && (
          <div className="px-4 -mt-2">
            <button
              onClick={() => {
                navigate("/results", {
                  state: {
                    originalImage: uploadedImage,
                    transformedImages: loadedVariations,
                    roomType,
                    designStyle: selectedStyle,
                    savedDesignId: loadedProjectId,
                    isAlreadyShared: loadedIsPublic,
                  },
                });
              }}
              className="flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <Eye className="h-4 w-4" />
              View {loadedVariations.length} generated result{loadedVariations.length > 1 ? "s" : ""}
            </button>
          </div>
        )}

        {/* Selectors Row */}
        <section className="px-4">
          <div className="flex gap-3">
            {/* Room Type */}
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1.5 block">
                Choose Your Room Type <span className="text-destructive">*</span>
                {detectingRoom && !roomType && (
                  <span className="ml-1 text-primary animate-pulse">· Detecting…</span>
                )}
              </Label>
              <MobileScrollSelect
                value={roomType}
                onValueChange={setRoomType}
                options={ROOM_TYPES}
                placeholder={detectingRoom ? "Detecting room…" : "Select Room Type"}
              />
              {roomType === "Floor Plan / 3D Render" && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">We'll preserve your layout and style each zone.</p>
              )}
            </div>

            {/* Variations */}
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1.5 block">
                Variations
                {subscriptionTier === 'free' && !isAdmin && (
                  <span className="ml-1.5 font-medium text-primary">· Pro for 2+</span>
                )}
              </Label>
              <Select
                value={variations}
                onValueChange={(value) => {
                  if (subscriptionTier === 'free' && !isAdmin && value !== "1") {
                    setVariations("1");
                    toast.error("Pro required", {
                      description: "Multiple variations are available on the Pro plan.",
                    });
                    setLockedStyleName(undefined);
                    setUpgradeContext("variation");
                    setUpgradeOpen(true);
                    return;
                  }
                  setVariations(value);
                }}
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VARIATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label} ({option.credits} credits)
                      {subscriptionTier === 'free' && !isAdmin && option.value !== "1" && (
                        <span className="ml-1 text-muted-foreground">Pro</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Category Carousel */}
        <section>
          <h3 className="text-sm font-medium text-foreground px-4 mb-2">Select Your Design Categories & Styles <span className="text-destructive">*</span></h3>
          <CategoryCarousel
            categories={DESIGN_CATEGORIES}
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
            loading={loadingUser}
            lockEnforced={lockEnforced}
            freeCounts={Object.fromEntries(
              DESIGN_CATEGORIES.map((c) => [
                c.id,
                c.styles.filter((s) => s.isFree).length,
              ])
            )}
          />
        </section>

        {/* Sub-Styles Grid */}
        {currentCategory && (
          <section className="px-4">
            <h3 className="text-sm font-medium text-foreground mb-2">{currentCategory.name} Styles</h3>
            <div className="bg-white rounded-2xl shadow-md p-3">
              <StyleGrid styles={currentCategory.styles} selectedStyle={selectedStyle} onSelect={handleStyleSelect} />
            </div>
          </section>
        )}

        {/* Custom Ideas */}
        <section className="px-4">
          <Label className="text-sm font-medium text-foreground">Custom Ideas (Optional)</Label>
          <Textarea
            value={customIdeas}
            onChange={(e) => setCustomIdeas(e.target.value)}
            placeholder="Add details (e.g., indoor plants, warm lighting, artwork)"
            className="mt-2 min-h-[80px] resize-none bg-white text-base"
          />
        </section>
      </div>

      {/* Fixed bottom dock: action button stacked above the tab bar */}
      <div className="fixed bottom-0 inset-x-0 z-40">
        <div className="bg-[#FFF7F1] border-t border-border p-2">
          <Button
            onClick={handleGenerate}
            className={`w-full h-12 text-base font-semibold rounded-full ${
              canGenerate ? "bg-[#7AB36B] hover:bg-green-600" : "bg-[#7AB36B]/50 cursor-not-allowed"
            }`}
          >
            <Sparkles className="h-5 w-5" />
            {generating ? "Generating..." : "Generate Design"}
          </Button>
          {!loadingUser && credits < parseInt(variations) && (
            <p className="text-xs text-destructive text-center mt-2">Not enough credits. You have {credits} credits.</p>
          )}
        </div>
        {/* Bottom tab navigation — only renders when the Room Refresh flag is visible. */}
        <BottomTabBar />
      </div>


      {/* Upgrade-to-Pro prompt for locked style attempts. */}
      <UpgradeProDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        styleName={lockedStyleName}
        context={upgradeContext}
      />

      {/* Mandatory feedback gate for free users before their 2nd generation. */}
      <GenerationSurveyDialog
        open={surveyOpen}
        onOpenChange={setSurveyOpen}
        userId={user?.id ?? ""}
        userEmail={user?.email}
        userName={user?.user_metadata?.full_name}
        onSubmitted={() => {
          markCompleted();
          pendingGenerationRef.current = true;
          setPendingGeneration(true);
          setSurveyOpen(false);
          handleGenerate();
        }}
      />
    </div>

  );
};

export default MobileDesigner;
